import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl, formatBRL, formatKM } from "@/lib/api";
import { useCompare } from "@/context/CompareContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft, X, Loader2, GitCompareArrows, Trophy } from "lucide-react";

const ROWS = [
  { key: "preco", label: "Preço", fmt: (v) => formatBRL(v), best: "min" },
  { key: "ano", label: "Ano", fmt: (v) => v, best: "max" },
  { key: "km", label: "Quilometragem", fmt: (v) => formatKM(v), best: "min" },
  { key: "cambio", label: "Câmbio", fmt: (v) => v || "—" },
  { key: "combustivel", label: "Combustível", fmt: (v) => v || "—" },
  { key: "cor", label: "Cor", fmt: (v) => v || "—" },
  { key: "cidade", label: "Localização", fmt: (v, c) => (v ? `${v}${c.estado ? " - " + c.estado : ""}` : "—") },
];

export default function Comparar() {
  const { ids, remove, clear } = useCompare();
  const navigate = useNavigate();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all(ids.map((id) => api.get(`/listings/${id}`).then((r) => r.data).catch(() => null)))
      .then((res) => { if (active) setCars(res.filter(Boolean)); })
      .finally(() => { if (active) setLoading(false); });
  }, [ids]);

  const bestId = (row) => {
    if (!row.best || cars.length < 2) return null;
    const vals = cars.map((c) => ({ id: c.id, v: c[row.key] })).filter((x) => typeof x.v === "number");
    if (!vals.length) return null;
    const target = row.best === "min" ? Math.min(...vals.map((x) => x.v)) : Math.max(...vals.map((x) => x.v));
    const winners = vals.filter((x) => x.v === target);
    return winners.length === 1 ? winners[0].id : null; // only highlight a unique winner
  };

  if (loading) return <div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-blaze" /></div>;

  if (!cars.length) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-24 text-center" data-testid="comparar-empty">
        <GitCompareArrows className="w-10 h-10 text-muted-foreground mx-auto" strokeWidth={1.5} />
        <h1 className="mt-4 font-heading font-800 text-2xl">Nenhum carro para comparar</h1>
        <p className="mt-2 text-muted-foreground">Escolha 2 ou 3 carros na busca usando o botão "Comparar".</p>
        <Button className="mt-6 bg-obsidian hover:bg-blaze text-white rounded-sm" onClick={() => navigate("/buscar")} data-testid="comparar-buscar-btn">Buscar carros</Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8" data-testid="comparar-page">
      <button onClick={() => navigate("/buscar")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-obsidian mb-4" data-testid="comparar-back">
        <ArrowLeft className="w-4 h-4" /> Voltar à busca
      </button>
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <p className="overline text-blaze mb-1">Lado a lado</p>
          <h1 className="font-heading font-800 text-3xl md:text-4xl">Comparar carros</h1>
        </div>
        <Button variant="ghost" onClick={clear} data-testid="comparar-clear-all"><X className="w-4 h-4 mr-1" /> Limpar tudo</Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse min-w-[560px]">
          <thead>
            <tr>
              <th className="w-32 sm:w-40" />
              {cars.map((c) => (
                <th key={c.id} className="p-2 align-top" data-testid={`compare-col-${c.id}`}>
                  <div className="bg-white border border-border rounded-sm overflow-hidden">
                    <div className="relative aspect-[4/3] bg-secondary">
                      <img src={c.images?.[0] ? fileUrl(c.images[0]) : "https://images.unsplash.com/photo-1630356220662-4d9804d3a987"} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => remove(c.id)} className="absolute top-1.5 right-1.5 w-7 h-7 rounded-sm bg-obsidian/80 text-white flex items-center justify-center hover:bg-blaze" data-testid={`compare-remove-${c.id}`}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="p-2 font-700 text-sm text-left leading-tight">{c.marca} {c.modelo}</p>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row) => {
              const winner = bestId(row);
              return (
                <tr key={row.key} className="border-t border-border">
                  <td className="py-3 pr-3 text-xs uppercase tracking-wide text-muted-foreground font-700 align-middle">{row.label}</td>
                  {cars.map((c) => (
                    <td key={c.id} className="py-3 px-2 text-center align-middle" data-testid={`compare-${row.key}-${c.id}`}>
                      <span className={`inline-flex items-center gap-1 font-600 ${winner === c.id ? "text-green-700" : "text-obsidian"}`}>
                        {winner === c.id && <Trophy className="w-3.5 h-3.5" />}
                        <span className={row.key === "preco" ? "font-heading font-800 text-base" : ""}>{row.fmt(c[row.key], c)}</span>
                      </span>
                    </td>
                  ))}
                </tr>
              );
            })}
            <tr className="border-t border-border">
              <td />
              {cars.map((c) => (
                <td key={c.id} className="py-4 px-2 text-center">
                  <Button size="sm" variant="outline" className="rounded-sm border-obsidian/15" onClick={() => navigate(`/anuncio/${c.id}`)} data-testid={`compare-ver-${c.id}`}>Ver anúncio</Button>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1"><Trophy className="w-3.5 h-3.5 text-green-700" /> destaque em verde = melhor valor (menor preço/km, ano mais novo).</p>
    </div>
  );
}
